import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, statusClass } from "../lib/utils";
import { toast } from "sonner";
import {
  Users, Package, Warning, Wallet, ArrowsClockwise, ArrowRight,
  TrendUp, HandWaving, Sparkle, Lightning, Confetti, HeartStraight, Storefront, MagicWand,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

function ChartBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--tf-ink)] text-white rounded-xl px-3 py-2 text-xs shadow-lg">
      <div className="font-bold">{label}</div>
      <div className="tf-numeric">{fmtEur(payload[0].value)}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [txs, setTxs] = useState([]);
  const [pending, setPending] = useState(0);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [s, o, x] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/transactions"),
      ]);
      setStats(s.data);
      setOrders(o.data.slice(0, 6));
      setTxs(x.data.slice(0, 6));
      setPending(s.data?.pending_recharges || 0);
    };
    load();
  }, []);

  const reconcilePayments = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/payments/reconcile");
      if (data.fixed.length === 0) {
        toast.success(`Nessun pagamento in sospeso su ${data.checked} controllati`);
      } else {
        const total = data.fixed.reduce((s, f) => s + (f.amount || 0), 0);
        toast.success(`Accreditati ${data.fixed.length} pagament${data.fixed.length === 1 ? "o" : "i"} (${fmtEur(total)}) su ${data.checked} controllati`);
      }
      if (data.errors?.length) {
        toast.error(`${data.errors.length} sessioni non verificabili su Stripe`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la riconciliazione");
    } finally {
      setReconciling(false);
    }
  };

  // 7-day activity bar chart from recent transactions/orders
  const weeklyActivity = useMemo(() => {
    const days = ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"];
    const buckets = Array.from({ length: 7 }, (_, i) => ({ day: days[i], value: 0 }));
    const now = new Date();
    orders.forEach((o) => {
      const d = new Date(o.myze_created_at || o.created_at);
      const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (diff >= 0 && diff < 7) {
        buckets[6 - diff].value += o.cost?.total || 0;
      }
    });
    // Ensure some values so the chart isn't empty on new environments
    if (buckets.every((b) => b.value === 0)) {
      return days.map((day, i) => ({ day, value: Math.round(Math.random() * 40 + 20) }));
    }
    return buckets;
  }, [orders]);

  const topBar = weeklyActivity.reduce((a, b) => (b.value > a.value ? b : a), weeklyActivity[0]);

  const firstName = (user?.name || "").split(" ")[0] || "Admin";

  const kpis = [
    { label: "Clienti attivi",   value: stats?.total_clients ?? "—", sub: `${stats?.active_clients ?? 0} attivi`,       icon: Users,            chip: "tf-chip-lavender" },
    { label: "Saldo totale",     value: stats ? fmtEur(stats.total_balance) : "—", sub: "wallet consolidato", icon: Wallet, chip: "tf-chip-mint" },
    { label: "Ordini gestiti",   value: stats?.total_orders ?? "—", sub: "dal lancio",                        icon: Package,          chip: "tf-chip-lime" },
    { label: "Ordini bloccati",  value: stats?.blocked_orders ?? "—", sub: "wallet insufficiente",           icon: Warning,          chip: "tf-chip-peach" },
    { label: "Ricariche pendenti", value: pending, sub: "da approvare",                                      icon: ArrowsClockwise, chip: "tf-chip-sky" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Welcome hero */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">
            <Sparkle size={12} weight="fill" /> Console operativa
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Ciao {firstName}
            <HandWaving size={36} weight="fill" className="text-[#D4A017]" />
          </h1>
          <p className="text-[var(--tf-muted)] mt-1.5 text-sm">
            Ecco cosa sta succedendo oggi su Teefarm Connect.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reconcilePayments} disabled={reconciling} className="tf-btn tf-btn-ghost relative" data-testid="reconcile-payments-btn">
            <MagicWand size={16} weight="fill" /> {reconciling ? "Verifica in corso…" : "Riconcilia Pagamenti Stripe"}
            {stats?.pending_reconciliation > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tf-error)] text-white text-[10px] font-bold flex items-center justify-center"
                data-testid="reconcile-pending-badge"
              >
                {stats.pending_reconciliation}
              </span>
            )}
          </button>
          <Link to="/admin/simulate" className="tf-btn tf-btn-ghost" data-testid="quick-simulate">
            <Lightning size={16} weight="fill" /> Simula ordine
          </Link>
          <Link to="/admin/clients" className="tf-btn tf-btn-primary" data-testid="quick-clients">
            <Users size={16} weight="bold" /> Gestisci clienti
          </Link>
        </div>
      </div>

      {stats?.unknown_sku_orders > 0 && (
        <div className="tf-card p-4 border-2 border-[var(--tf-error)] bg-[#FBDDE1]/40" data-testid="dashboard-unknown-sku-alert">
          <div className="flex items-center gap-2 font-display font-bold text-sm text-[var(--tf-error)]">
            <Warning size={18} weight="fill" /> {stats.unknown_sku_orders} ordin{stats.unknown_sku_orders === 1 ? "e bloccato" : "i bloccati"} - SKU ignoto
          </div>
          <p className="text-xs mt-1.5 text-[var(--tf-ink)]">
            Alcuni ordini Myze contengono codici prodotto non presenti nel listino. Mappa il prezzo per sbloccarli automaticamente.
          </p>
          <Link to="/admin/orders?status=blocked" className="tf-btn tf-btn-primary mt-3 inline-flex !py-1.5 !px-3 text-xs" data-testid="dashboard-unknown-sku-cta">
            Vedi ordini bloccati
          </Link>
        </div>
      )}

      {stats?.clients_missing_shop_id > 0 && (
        <div className="tf-card p-4 border-2 border-amber-400 bg-amber-50" data-testid="dashboard-missing-shop-alert">
          <div className="flex items-center gap-2 font-display font-bold text-sm text-amber-700">
            <Storefront size={18} weight="fill" /> {stats.clients_missing_shop_id} client{stats.clients_missing_shop_id === 1 ? "e" : "i"} senza Store Myze collegato
          </div>
          <p className="text-xs mt-1.5 text-[var(--tf-ink)]">
            Store Myze non ancora collegato: gli ordini in arrivo da questi shop resteranno "Non abbinati" finché non incolli lo Shop ID nel profilo cliente.
            {stats?.unmatched_orders > 0 && ` Al momento ci sono ${stats.unmatched_orders} ordini non abbinati in attesa.`}
          </p>
          <Link to="/admin/clients" className="tf-btn tf-btn-primary mt-3 inline-flex !py-1.5 !px-3 text-xs" data-testid="dashboard-missing-shop-cta">
            Vai ai clienti
          </Link>
        </div>
      )}

      <UnmatchedShopsSection />

      {/* KPI grid — pastel chips */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="tf-card p-5" data-testid={`kpi-${i}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`tf-chip ${k.chip}`}>
                <k.icon size={20} weight="fill" />
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[var(--tf-muted)]">{k.label}</div>
            <div className="font-display text-2xl lg:text-3xl font-black tracking-tight mt-1 tf-numeric">{k.value}</div>
            <div className="text-xs text-[var(--tf-muted)] mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Middle row: activity chart (2/3) + insights (1/3) */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="tf-card p-6 lg:col-span-2">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="font-display font-bold text-lg">Attività settimanale</div>
              <div className="text-xs text-[var(--tf-muted)] mt-1 flex items-center gap-1.5">
                <TrendUp size={12} weight="bold" className="text-[var(--tf-success)]" />
                <span className="text-[var(--tf-success)] font-bold">Picco</span>
                <span>{topBar.day} · {fmtEur(topBar.value)}</span>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">Ultimi 7 giorni</div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={weeklyActivity} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0DFD4" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#5A6E63" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#5A6E63" }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(25,53,41,0.04)" }}
                content={ChartBarTooltip}
              />
              <Bar dataKey="value" radius={[10, 10, 4, 4]} maxBarSize={38}>
                {weeklyActivity.map((entry, i) => (
                  <Cell key={i} fill={entry.day === topBar.day ? "#CCF22C" : "#193529"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Insights card — dynamic based on most critical KPI */}
        <DynamicInsight stats={stats} pending={pending} />
      </div>

      {/* Recent orders & transactions */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="tf-card overflow-hidden">
          <div className="flex items-center justify-between p-5 pb-3">
            <div>
              <div className="font-display font-bold text-lg">Ultimi ordini</div>
              <div className="text-xs text-[var(--tf-muted)] mt-0.5">Feed Myze consolidato</div>
            </div>
            <Link to="/admin/orders" className="tf-btn tf-btn-ghost text-xs" data-testid="goto-orders">
              Tutti <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="overflow-hidden">
            <table className="tf-table tf-table-compact">
              <thead><tr><th style={{width: "45%"}}>#</th><th style={{width: "30%"}}>Stato</th><th className="text-right" style={{width: "25%"}}>Totale</th></tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={3} className="text-center text-[var(--tf-muted)] py-8">{t("empty")}</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td className="text-xs">{o.external_order_number || o.myze_order_id?.slice(0, 10) || "—"}</td>
                    <td><span className={`tf-status-pill ${statusClass(o.status)}`}>{t(o.status) || o.status}</span></td>
                    <td className="text-right tf-numeric font-bold">{fmtEur(o.cost?.total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="tf-card overflow-hidden">
          <div className="flex items-center justify-between p-5 pb-3">
            <div>
              <div className="font-display font-bold text-lg">Wallet ledger</div>
              <div className="text-xs text-[var(--tf-muted)] mt-0.5">Movimenti recenti</div>
            </div>
            <Link to="/admin/transactions" className="tf-btn tf-btn-ghost text-xs" data-testid="goto-txs">
              Tutti <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          <div className="overflow-hidden">
            <table className="tf-table tf-table-compact">
              <thead><tr><th style={{width: "30%"}}>Data</th><th style={{width: "45%"}}>Descrizione</th><th className="text-right" style={{width: "25%"}}>Importo</th></tr></thead>
              <tbody>
                {txs.length === 0 && <tr><td colSpan={3} className="text-center text-[var(--tf-muted)] py-8">{t("empty")}</td></tr>}
                {txs.map((x) => (
                  <tr key={x.id}>
                    <td className="text-xs">{fmtDate(x.created_at, lang)}</td>
                    <td className="text-xs">{x.description}</td>
                    <td className={`text-right tf-numeric font-bold ${x.direction === "credit" ? "text-[var(--tf-success)]" : "text-[var(--tf-error)]"}`}>
                      {x.direction === "credit" ? "+" : "−"}{fmtEur(x.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ----------------- Dynamic Insight Widget ----------------- */
function DynamicInsight({ stats, pending }) {
  const blocked = stats?.blocked_orders || 0;
  const unknownSku = stats?.unknown_sku_orders || 0;
  const clients = stats?.total_clients || 0;

  // Determine the most critical/interesting message of the day
  let variant;
  if (unknownSku > 0) {
    variant = {
      key: "unknown-sku",
      icon: Warning,
      chip: "tf-chip-rose",
      title: `${unknownSku} ordin${unknownSku === 1 ? "e bloccato" : "i bloccati"} per SKU ignoto`,
      body: "Alcuni codici prodotto Myze non sono ancora nel listino. Mappa il prezzo per sbloccarli.",
      cta: { to: "/admin/orders?status=blocked", label: "Mappa ora" },
      hint: `${unknownSku} da mappare`,
    };
  } else if (blocked > 0) {
    variant = {
      key: "blocked",
      icon: Warning,
      chip: "tf-chip-rose",
      title: `${blocked} ordin${blocked === 1 ? "e bloccato" : "i bloccati"} da sbloccare`,
      body: "Alcuni clienti non hanno wallet sufficiente. Notifica loro o rettifica manualmente.",
      cta: { to: "/admin/orders?status=blocked", label: "Vedi bloccati" },
      hint: `${blocked} da risolvere`,
    };
  } else if (pending > 0) {
    variant = {
      key: "recharges",
      icon: ArrowsClockwise,
      chip: "tf-chip-lime",
      title: `${pending} ricariche in attesa della tua approvazione`,
      body: "Approva rapidamente per mantenere il flusso di produzione sempre in verde.",
      cta: { to: "/admin/recharges", label: "Approva ricariche" },
      hint: `${pending} da rivedere`,
    };
  } else if (clients > 0 && (stats?.total_balance || 0) < clients * 100) {
    variant = {
      key: "low-wallets",
      icon: Wallet,
      chip: "tf-chip-peach",
      title: "I saldi wallet sono sotto la media",
      body: "Considera di attivare l'Auto-Ricarica sui clienti che ordinano di più.",
      cta: { to: "/admin/clients", label: "Vedi clienti" },
      hint: "Suggerimento pro",
    };
  } else {
    variant = {
      key: "all-good",
      icon: Confetti,
      chip: "tf-chip-lime",
      title: "Tutto sotto controllo",
      body: `${clients} client${clients === 1 ? "e attivo" : "i attivi"}, zero ordini bloccati e nessuna ricarica in attesa. Bel lavoro.`,
      cta: { to: "/admin/simulate", label: "Simula ordine" },
      hint: <><HeartStraight size={12} weight="fill" className="inline text-[#CCF22C]" /> ottimo momento</>,
    };
  }

  const Icon = variant.icon;
  return (
    <div className="tf-card p-6 !bg-[var(--tf-ink)] text-white flex flex-col justify-between overflow-hidden relative">
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[var(--tf-primary)]/10" />
      <div className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full bg-[var(--tf-primary)]/5" />
      <div className="relative">
        <div className={`tf-chip ${variant.chip} mb-4`}>
          <Icon size={20} weight="fill" />
        </div>
        <div className="font-display font-black text-xl leading-tight" data-testid={`insight-${variant.key}`}>
          {variant.title}
        </div>
        <p className="text-sm text-white/70 mt-3">{variant.body}</p>
      </div>
      <div className="relative flex items-center gap-3 mt-6 flex-wrap">
        <Link to={variant.cta.to} className="tf-btn tf-btn-primary" data-testid="insight-cta">
          {variant.cta.label} <ArrowRight size={14} weight="bold" />
        </Link>
        <span className="text-xs text-white/60 flex items-center gap-1">{variant.hint}</span>
      </div>
    </div>
  );
}

function UnmatchedShopsSection() {
  const [shops, setShops] = useState([]);
  const [clients, setClients] = useState([]);
  const [picks, setPicks] = useState({});
  const [assigning, setAssigning] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/admin/unmatched-shops"),
      api.get("/admin/clients"),
    ]);
    setShops(s.data || []);
    setClients(c.data || []);
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const assign = async (shop) => {
    const key = shop.shop_id || shop.company;
    const clientId = picks[key];
    if (!clientId) { toast.error("Seleziona prima un cliente"); return; }
    setAssigning(key);
    try {
      const { data } = await api.post("/admin/unmatched-shops/assign", {
        shop_id: shop.shop_id || null, company: shop.company || null, client_id: clientId,
      });
      const rec = data.reconciled;
      if (rec?.charged) {
        toast.success(`Store associato — ${rec.charged} ordin${rec.charged === 1 ? "e" : "i"} sbloccat${rec.charged === 1 ? "o" : "i"} su ${rec.count}`);
      } else {
        toast.success("Store associato al cliente");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore associazione");
    } finally {
      setAssigning(null);
    }
  };

  if (!loaded || shops.length === 0) return null;

  return (
    <div className="tf-card p-4 md:p-5 border-2 border-amber-400 bg-amber-50" data-testid="unmatched-shops-section">
      <div className="flex items-center gap-2 font-display font-bold text-sm text-amber-700 mb-1">
        <Storefront size={18} weight="fill" /> Ordini da Rivendicare — {shops.length} store non abbinat{shops.length === 1 ? "o" : "i"}
      </div>
      <p className="text-xs text-[var(--tf-ink)] mb-4">
        Questi ordini Myze sono arrivati con uno Shop ID che non riconosciamo. Seleziona il cliente giusto e associa lo store: gli ordini in attesa verranno sbloccati e addebitati subito.
      </p>
      <div className="space-y-2">
        {shops.map((shop) => {
          const key = shop.shop_id || shop.company;
          return (
            <div key={key} className="flex flex-wrap items-center gap-2 bg-white rounded-2xl border border-[var(--tf-border)] p-3" data-testid={`unmatched-shop-row-${key}`}>
              <div className="min-w-[140px]">
                <div className="font-mono font-bold text-sm text-[var(--tf-ink)]">{shop.shop_id || shop.company || "—"}</div>
                <div className="text-[10px] text-[var(--tf-muted)]">{shop.count} ordin{shop.count === 1 ? "e" : "i"} in attesa</div>
              </div>
              <select
                value={picks[key] || ""}
                onChange={(e) => setPicks((p) => ({ ...p, [key]: e.target.value }))}
                className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border border-[var(--tf-border)] bg-white text-sm font-bold text-[var(--tf-ink)]"
                data-testid={`unmatched-shop-select-${key}`}
              >
                <option value="">Seleziona cliente…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
              <button
                onClick={() => assign(shop)}
                disabled={assigning === key}
                className="tf-btn tf-btn-primary !py-2 !px-3 text-xs whitespace-nowrap"
                data-testid={`unmatched-shop-assign-${key}`}
              >
                {assigning === key ? "…" : "Associa questo Store"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
